<template>
  <div
    class="relationship-path-rail"
    :class="{ 'relationship-path-rail--dense': dense }"
    role="group"
    :aria-label="ariaLabel">
    <v-expansion-panels
      v-if="hasDetail"
      v-model="openPanel"
      accordion
      flat
      tile
      class="relationship-path-rail__panels">
      <v-expansion-panel class="relationship-path-rail__panel">
        <v-expansion-panel-header
          class="relationship-path-rail__header"
          :aria-label="detailToggleLabel"
          @click.native="userToggled = true">
          <span class="relationship-path-rail__sentence">
            <template v-for="part in sentenceParts">
              <span
                v-if="part.kind === 'token'"
                :key="part.key"
                class="relationship-path-rail__token"
                :class="{ 'relationship-path-rail__token--ghost': part.ghost }"
                :title="part.text">
                <v-icon v-if="part.icon && !part.ghost" small>{{ part.icon }}</v-icon>
                <span class="relationship-path-rail__token-label">{{ part.text }}</span>
              </span>
              <span
                v-else
                :key="part.key"
                class="relationship-path-rail__word"
                :class="`relationship-path-rail__word--${part.kind}`">{{ part.text }}</span>
            </template>
          </span>
        </v-expansion-panel-header>
        <v-expansion-panel-content class="relationship-path-rail__detail">
          <p class="relationship-path-rail__detail-head">Synchronized fields</p>
          <table class="relationship-path-rail__mappings">
            <colgroup>
              <col class="relationship-path-rail__col--field" />
              <col class="relationship-path-rail__col--arrow" />
              <col class="relationship-path-rail__col--field" />
              <col class="relationship-path-rail__col--type" />
            </colgroup>
            <thead>
              <tr>
                <th scope="col">Register field</th>
                <th scope="col"><span class="ds-sr-only">synchronized with</span></th>
                <th scope="col">Target property</th>
                <th scope="col"><span class="ds-sr-only">Type</span></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="projection in projections" :key="projection.key">
                <td>
                  <span class="relationship-path-rail__map-token" :title="projection.source">
                    <span class="relationship-path-rail__token-label">{{ projection.source }}</span>
                  </span>
                </td>
                <td class="relationship-path-rail__arrow-cell">
                  <v-icon x-small aria-hidden="true">mdi-swap-horizontal</v-icon>
                </td>
                <td>
                  <span class="relationship-path-rail__map-token" :title="projection.destination">
                    <span class="relationship-path-rail__token-label">{{ projection.destination }}</span>
                  </span>
                </td>
                <td class="relationship-path-rail__type-cell">
                  <v-chip v-if="projection.namespace" x-small outlined>{{ projection.namespace }}</v-chip>
                </td>
              </tr>
            </tbody>
          </table>
          <p class="relationship-path-rail__detail-note">{{ resolutionStatement }}</p>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>

    <span v-else class="relationship-path-rail__sentence relationship-path-rail__sentence--static">
      <template v-for="part in sentenceParts">
        <span
          v-if="part.kind === 'token'"
          :key="part.key"
          class="relationship-path-rail__token"
          :class="{ 'relationship-path-rail__token--ghost': part.ghost }"
          :title="part.text">
          <v-icon v-if="part.icon && !part.ghost" small>{{ part.icon }}</v-icon>
          <span class="relationship-path-rail__token-label">{{ part.text }}</span>
        </span>
        <span
          v-else
          :key="part.key"
          class="relationship-path-rail__word"
          :class="`relationship-path-rail__word--${part.kind}`">{{ part.text }}</span>
      </template>
    </span>
  </div>
</template>

<script>
export default {
  name: 'RelationshipPathRail',
  props: {
    source: { type: String, default: '' },
    target: { type: String, default: '' },
    matchField: { type: String, default: '' },
    resolutionMode: { type: String, default: 'Resolve only' },
    projections: { type: Array, default: () => [] },
    dense: { type: Boolean, default: false },
    // Confirm-before-write surfaces open the detail up front: a run must never be approved with
    // its field mappings hidden behind a disclosure.
    defaultExpanded: { type: Boolean, default: false },
    // Opens the detail the first time mappings appear, so the wizard shows what it just built.
    // Opt-in: without it a list of cards would all spring open as their data loads.
    autoExpand: { type: Boolean, default: false },
  },
  data() {
    return {
      openPanel: this.defaultExpanded ? 0 : null,
      userToggled: false,
    };
  },
  computed: {
    hasDetail() { return this.projections.length > 0; },
    sourceText() { return this.source || 'source field'; },
    targetText() { return this.target || 'target record'; },
    matchFieldText() { return this.matchField || 'match field'; },
    resolutionPhrase() {
      const mode = this.resolutionMode || '';
      return mode ? `${mode.charAt(0).toLowerCase()}${mode.slice(1)}` : '';
    },
    syncPhrase() {
      const count = this.projections.length;
      if (!count) return 'keeps no fields in sync';
      return `keeps ${count} ${count === 1 ? 'field' : 'fields'} in sync`;
    },
    isIncomplete() { return !this.source || !this.target || !this.matchField; },
    // The sentence is data so both the panel header and the static fallback render one definition.
    sentenceParts() {
      const parts = [
        {
          key: 'source',
          kind: 'token',
          icon: 'mdi-table-column',
          text: this.sourceText,
          ghost: !this.source,
        },
        { key: 'matches', kind: 'word', text: 'matches' },
        {
          key: 'target',
          kind: 'token',
          icon: 'mdi-target-account',
          text: this.targetText,
          ghost: !this.target,
        },
        { key: 'by', kind: 'word', text: 'by' },
        { key: 'match-field', kind: 'token', text: this.matchFieldText, ghost: !this.matchField },
      ];

      if (this.resolutionPhrase) {
        parts.push({ key: 'sep-1', kind: 'separator', text: '·' });
        parts.push({ key: 'resolution', kind: 'word', text: this.resolutionPhrase });
      }
      parts.push({ key: 'sep-2', kind: 'separator', text: '·' });
      // While a token is still a placeholder, a synchronized-field count is noise; the sentence says what it is
      // waiting for instead. Phrased without "form" because cards and the run dialog share this.
      parts.push(this.isIncomplete
        ? { key: 'hint', kind: 'hint', text: 'not yet complete' }
        : { key: 'sync', kind: 'word', text: this.syncPhrase });
      return parts;
    },
    resolutionStatement() {
      const match = this.matchField ? `Match field: ${this.matchField}` : 'Match field not selected';
      return `${match} - ${this.resolutionMode}`;
    },
    sentenceText() {
      return this.sentenceParts
        .filter((part) => part.kind !== 'separator')
        .map((part) => part.text)
        .join(' ');
    },
    detailToggleLabel() {
      const count = this.projections.length;
      return `Synchronized fields: ${count} ${count === 1 ? 'field' : 'fields'}`;
    },
    ariaLabel() {
      return `Relationship path. ${this.sentenceText}.`;
    },
  },
  watch: {
    'projections.length': function watchProjectionCount(next, previous) {
      if (!this.autoExpand || this.userToggled) return;
      if (previous === 0 && next > 0) this.openPanel = 0;
    },
  },
};
</script>

<style lang="scss" scoped>
.relationship-path-rail {
  --rail-muted: var(--relationship-muted, #59635f);

  min-width: 0;
  border: 1px solid var(--ds-border-default);
  border-radius: var(--ds-radius-md);
  overflow: hidden;
  background: var(--v-bg3-base);
}

.relationship-path-rail__sentence {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  min-width: 0;
  gap: 4px 7px;
  color: var(--rail-muted);
  font-family: var(--ds-font-family-body);
  font-size: var(--ds-font-body-sm);
  line-height: 1.7;
}

.relationship-path-rail__sentence--static {
  padding: 10px var(--ds-space-md);
}

.relationship-path-rail__token {
  display: inline-flex;
  align-items: center;
  max-width: 220px;
  min-width: 0;
  gap: 6px;
  padding: 2px 8px;
  border: 1px solid var(--ds-border-strong);
  border-radius: var(--ds-radius-sm);
  color: var(--v-text-base);
  background: var(--ds-surface-elevated);
  font-size: var(--ds-font-caption);
  font-weight: 600;
}

.relationship-path-rail__token .v-icon {
  flex: 0 0 auto;
  color: var(--v-primary-base);
}

.relationship-path-rail__token-label {
  overflow: hidden;
  min-width: 0;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.relationship-path-rail__token--ghost {
  border-style: dashed;
  border-color: var(--ds-border-default);
  color: var(--rail-muted);
  background: transparent;
  font-weight: 400;
  font-style: italic;
}

.relationship-path-rail__word { white-space: nowrap; }
.relationship-path-rail__word--separator { color: var(--ds-border-strong); user-select: none; }
.relationship-path-rail__word--hint { font-style: italic; }

.relationship-path-rail__panels { background: transparent; }

.relationship-path-rail__panel::before { box-shadow: none; }

.relationship-path-rail__panel,
.relationship-path-rail__panel.v-expansion-panel--active { background: transparent; }

.relationship-path-rail__header {
  min-height: 0;
  padding: 10px var(--ds-space-md);
}

.relationship-path-rail__header.v-expansion-panel-header--active { min-height: 0; }

.relationship-path-rail__header ::v-deep .v-expansion-panel-header__icon .v-icon {
  color: var(--rail-muted) !important;
}

.relationship-path-rail__detail ::v-deep .v-expansion-panel-content__wrap {
  padding: 0 var(--ds-space-md) 12px;
  border-top: 1px solid var(--ds-border-subtle);
  background: var(--v-bg2-base);
}

.relationship-path-rail__detail-head {
  margin: 10px 0 6px;
  color: var(--rail-muted);
  font-size: var(--ds-font-micro);
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

/* table-layout: fixed with percentage columns holds one alignment down the list AND across the
   three container widths this rail renders in - the card at 700px, the wizard, and the run dialog. */
.relationship-path-rail__mappings {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
}

.relationship-path-rail__col--field { width: 37%; }
.relationship-path-rail__col--arrow { width: 26px; }
.relationship-path-rail__col--type { width: 22%; }

.relationship-path-rail__mappings th {
  padding: 0 10px 6px 0;
  border-bottom: 1px solid var(--ds-border-default);
  color: var(--rail-muted);
  text-align: left;
  font-family: var(--ds-font-family-heading);
  font-size: var(--ds-font-micro);
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.relationship-path-rail__mappings td {
  overflow: hidden;
  padding: 6px 10px 6px 0;
  border-bottom: 1px solid var(--ds-border-subtle);
  color: var(--v-text-base);
  vertical-align: middle;
  font-size: var(--ds-font-caption);
}

.relationship-path-rail__mappings tr:last-child td { border-bottom: 0; }
.relationship-path-rail__arrow-cell { overflow: visible; padding: 0; text-align: center; }
.relationship-path-rail__arrow-cell .v-icon { color: var(--rail-muted); }
.relationship-path-rail__type-cell { overflow: visible; padding-right: 0; }

/* Same chip language as the sentence tokens above, sized to its grid cell. The centered sync icon
   identifies the pair, while the type chip carries the remaining context. */
.relationship-path-rail__map-token {
  display: inline-flex;
  align-items: center;
  min-width: 0;
  max-width: 100%;
  padding: 2px 8px;
  border: 1px solid var(--ds-border-strong);
  border-radius: var(--ds-radius-sm);
  color: var(--v-text-base);
  background: var(--ds-surface-elevated);
  font-weight: 600;
}

.relationship-path-rail__type-cell .v-chip { max-width: 100%; }

.relationship-path-rail__detail-note {
  margin: var(--ds-space-sm) 0 0;
  color: var(--rail-muted);
  font-size: var(--ds-font-caption);
}

.relationship-path-rail--dense .relationship-path-rail__header,
.relationship-path-rail--dense .relationship-path-rail__sentence--static {
  padding: var(--ds-space-sm) var(--ds-space-md);
}

.relationship-path-rail--dense .relationship-path-rail__sentence { line-height: 1.55; }

@media (prefers-reduced-motion: reduce) {
  .relationship-path-rail *,
  .relationship-path-rail *::before,
  .relationship-path-rail *::after {
    transition-duration: 0.01ms !important;
    animation-duration: 0.01ms !important;
  }
}
</style>
